'use client';
import {
  GetCarBrandsOperation,
  GetCarFairMarketValueOperation,
  GetCarInsuranceQuoteOperation,
  GetCarModelsOperation,
  GetCarTrimsOperation,
  GetCarYearsOperation,
} from '__generated__/graphql';
import {
  MoneyMaxPremiumedProduct,
  MoneyMaxProductPremium,
} from 'car-insurance/types';
import { mapToLabelValue } from 'utils/mapToLabelValue';
import { createLazyQuery } from '../../hooks/useLazyQuery';
import { GetCarInsuranceQuoteMoneyMaxPayload } from './types';

class MoneyMaxClientApiService {
  public async getVehicleMakes() {
    try {
      const query = createLazyQuery(GetCarBrandsOperation);
      const response = await query({} as never);
      const brandsWithoutDuplicates = response?.getCarBrands?.filter(
        (item) => item !== 'Gac',
      );
      return brandsWithoutDuplicates as string[];
    } catch (error) {
      console.error('Error fetching car brands:', error);
      return [];
    }
  }

  public async getVehicleModels(makeName: string) {
    const query = createLazyQuery(GetCarModelsOperation);
    const response = await query({ brand: makeName });
    return mapToLabelValue(response?.getCarModels as string[]);
  }

  public async getVehicleYears(makeName: string, modelName: string) {
    const query = createLazyQuery(GetCarYearsOperation);
    const response = await query({ brand: makeName, model: modelName });
    return mapToLabelValue(response?.getCarYears as string[]);
  }

  public async getVehicleTrims(
    makeName: string,
    modelName: string,
    year: string | number,
  ) {
    const query = createLazyQuery(GetCarTrimsOperation);
    const response = await query({
      brand: makeName,
      model: modelName,
      year: Number(year),
    });
    return mapToLabelValue(response?.getCarTrims as string[]);
  }

  public async getVehicleFairMarketValue(
    makeName: string,
    modelName: string,
    year: string | number,
    trim: string,
  ) {
    const query = createLazyQuery(GetCarFairMarketValueOperation);
    const response = await query({
      brand: makeName,
      model: modelName,
      year: Number(year),
      trim: trim,
    });
    return {
      carId: response?.getCarFairMarketValue?.carId,
      fmv: response?.getCarFairMarketValue?.fairMarketValue,
    } as { carId: number; fmv: number };
  }

  public async getCarInsurancePremiums(
    props: GetCarInsuranceQuoteMoneyMaxPayload,
  ) {
    const query = createLazyQuery(GetCarInsuranceQuoteOperation);

    const fmv = await this.getVehicleFairMarketValue(
      props.makeName,
      props.modelName,
      props.year,
      props.trim,
    );

    // For some reason the API slices the last two digits
    const propertyDamageCoverage = Number(
      `${props.bodilyInjuryAndPropertyDamageCoverage}00`,
    );

    const premiums = (
      await query({
        request: {
          carId: Number(fmv.carId),
          fairMarketValue: fmv.fmv,
          personalAccidentCoverage: Number(props.personalAccidentCoverage),
          bodilyInjuryAndPropertyDamageCoverage: propertyDamageCoverage,
          includesActsOfNature: props.includesActsOfNature,
          usage: props.usage,
          loanWithBankId: 0,
        },
      })
    )?.getCarInsuranceQuoteRequest
      ?.quotes as unknown as MoneyMaxProductPremium[];

    const premiumedProducts = props.products.map((product) => {
      const productId = product.productUid;
      const productPremium = premiums.find(
        (premium) => premium.productId === productId,
      );

      if (productPremium) {
        return {
          ...product,
          premium: productPremium,
        } as MoneyMaxPremiumedProduct;
      }

      return { ...product, premium: undefined } as MoneyMaxPremiumedProduct;
    });

    return premiumedProducts;
  }
}

export const moneymaxClientApiService = new MoneyMaxClientApiService();
